/**
 * monitoring-coverage.ts — pure view-model composer for the Baseline
 * Compliance "Monitoring" tab (customer-facing coverage transparency +
 * setup journey).
 *
 * Turns the tenant-scoped `TcmCoverageSummary` wire DTO into everything the
 * tab renders: hero copy, the coverage-channel panels, per-group rows, the
 * Detect → Decide → Apply lifecycle strip, and the setup-journey stepper.
 * All conditional copy lives HERE, not in the component — the component test
 * harness is renderToStaticMarkup (effects never run), so this module is the
 * test seam. Vitest coverage in monitoring-coverage.test.ts.
 *
 * Voice rules (CLAUDE.md Product Voice + the locked copy doc
 * `.agents/Tasks/Not Started/tcm-customer-monitoring-scope-copy.md`):
 *   - Trusted advisor: verdicts first, named subjects, provenance bylines.
 *   - NO internal jargon, ever: the words TCM / DSC / UTCM / M365DSC /
 *     "monitor" (the Graph object) / "snapshot job" / instance budgets and
 *     ceilings must not appear in any string this module emits.
 *   - Never fabricate: a missing count or date renders as an honest state
 *     ("We're sizing your monitored scope"), not a 0 or an invented date.
 *   - Focus is framed as deliberate, never as an apology or a gap.
 */

import { format } from 'date-fns'

import { formatTcmResourceType } from '@/lib/tcmResourceNames'
import type {
  TcmCoverageSummary,
  TcmCoverageGroup,
  TcmMonitoringGroup,
  TcmOnboardingState,
} from '@/types/tcm'

/* -------------------------------------------------------------------------- */
/* View-model types                                                           */
/* -------------------------------------------------------------------------- */

export type CoverageViewState = 'loading' | 'no-tenant' | 'error' | 'ready'

export interface CoverageHero {
  /** First part of the section headline, e.g. "We watch what ". `headlineEm`
   *  follows it in the same upright heading. */
  headline: string
  /** The headline's tail, e.g. "matters.", rendered right after `headline`. */
  headlineEm: string
  /** Deck sentences in render order. Real data only — see composer rules. */
  deck: string[]
  /** Provenance meta after "Assessed by Guardian · ", e.g. "Scope sized June 3, 2026". */
  bylineMeta: string
}

export interface CoverageChannel {
  id: 'continuous' | 'scans' | 'capture'
  title: string
  /** Six words or fewer, the channel tile's first line under its title. */
  verdict: string
  body: string
  /** Meta line in tabular figures, real numbers/dates only. Null = no meta rendered. */
  meta: string | null
  active: boolean
  /** Rendered when !active — points at the journey, never apologises. */
  inactiveNote: string | null
  /** Optional caveat on an active channel (e.g. mail-side still completing). */
  caveat: string | null
}

export interface CoverageGroupRow {
  group: TcmMonitoringGroup
  title: string
  description: string
  /** true → watched dot; false → quiet "not in scope"; null → unknown. */
  watched: boolean | null
  /** "95 settings" — null renders silent. */
  countLabel: string | null
  /** The watched settings by type, in server order; null when not watched or not listed. */
  settings: CoverageGroupSettings[] | null
}

export interface CoverageGroupSettings {
  id: string
  /** Friendly type name, singular as the namer gives it ("Conditional Access policy"). */
  typeLabel: string
  count: number
  /** Distinct setting names, sorted by the server; may be fewer than `count`. */
  names: string[]
}

export interface LifecycleStep {
  numeral: string
  title: string
  body: string
}

export type JourneyStepState = 'done' | 'current' | 'todo'

export interface JourneyStep {
  id: 'connect' | 'verify' | 'size' | 'scope' | 'watch'
  title: string
  body: string
  state: JourneyStepState
}

export interface CoverageJourney {
  /** Verdict headline for where the tenant sits. */
  headline: string
  /** One supporting sentence under the headline. */
  sub: string
  steps: JourneyStep[]
  /** A stalled journey's plain-language caveat (never the raw server error). */
  caveat: string | null
}

export interface MonitoringCoverageView {
  state: CoverageViewState
  hero: CoverageHero
  channels: CoverageChannel[]
  /**
   * Group rows render only once continuous watch is live (pre-activation the
   * journey is the story and per-group counts don't exist yet). Null = the
   * section is omitted entirely.
   */
  groups: CoverageGroupRow[] | null
  /** Caveat above the group rows when the watch list could not be read. */
  groupsCaveat: string | null
  /** Always rendered under the groups (or channels when groups are absent). */
  inventoryNote: string
  lifecycle: LifecycleStep[]
  /** Null once monitoring is fully active — the channels tell the story. */
  journey: CoverageJourney | null
}

export interface ComposeCoverageInput {
  coverage: TcmCoverageSummary | undefined
  isLoading: boolean
  isError: boolean
  /** No tenant selected yet (PA "all tenants" context). */
  hasTenant: boolean
  /** Injectable clock for tests. */
  now?: Date
}

/* -------------------------------------------------------------------------- */
/* Customer copy — group names + descriptions                                 */
/* -------------------------------------------------------------------------- */

/**
 * Customer-quality titles + descriptions per monitoring group. Derived from
 * the monitoring-profile copy already shipped in
 * Policies/common/tcm_monitoring_profiles.json (same register, per-group).
 * `inventory` is deliberately absent — it gets the standing footnote instead.
 */
const GROUP_COPY: Readonly<
  Record<Exclude<TcmMonitoringGroup, 'inventory'>, { title: string; description: string }>
> = {
  identity: {
    title: 'Identity & access',
    description: 'Who can get in, from where, and how strongly they must prove it.',
  },
  threatProtection: {
    title: 'Threat protection',
    description: 'The controls that stop threats getting in and bad mail or data getting out.',
  },
  endpoint: {
    title: 'Endpoint & device',
    description: 'Whether your managed devices are held to the security bar.',
  },
  collaboration: {
    title: 'Collaboration',
    description: 'How open Teams is to the outside world.',
  },
  governance: {
    title: 'Governance & compliance',
    description: 'Data protection, retention, and privileged-access policy.',
  },
}

const INVENTORY_NOTE =
  'Your users, groups, and mailboxes are inventory, not configuration. ' +
  'They change by the hour, so we deliberately don’t flag them as drift.'

/* -------------------------------------------------------------------------- */
/* Formatting helpers (exported for tests)                                    */
/* -------------------------------------------------------------------------- */

/** "June 3, 2026" — null in, null out; invalid dates degrade to null. */
export function formatLongDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return format(d, 'MMMM d, yyyy')
}

/**
 * Relative age for "last check" phrasing: "moments ago", "35m ago",
 * "3h ago", "2d ago". Null/invalid → null (caller omits the clause).
 */
export function formatRelativeAge(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const mins = Math.floor((now.getTime() - then) / 60000)
  if (mins < 1) return 'moments ago'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/** "171 settings" / "1 setting" — null in, null out. Zero is honest. */
export function formatSettingCount(count: number | null | undefined): string | null {
  if (count == null) return null
  return count === 1 ? '1 setting' : `${count} settings`
}

/* -------------------------------------------------------------------------- */
/* Static strips                                                              */
/* -------------------------------------------------------------------------- */

/** Detect → Decide → Apply. Static, but lives here so copy is test-pinned. */
export const LIFECYCLE_STEPS: readonly LifecycleStep[] = [
  {
    numeral: '1',
    title: 'Guardian flags the change',
    body: 'You see which setting moved, its before and after values, and a severity.',
  },
  {
    numeral: '2',
    title: 'You decide',
    body:
      'Accept stages the new value as part of your approved baseline. ' +
      'Reject records your decision and keeps your baseline unchanged.',
  },
  {
    numeral: '3',
    title: 'Apply lands it',
    body: 'Accepted changes go through together as one baseline update. Nothing changes until you apply.',
  },
] as const

export const JOURNEY_STEP_ORDER = ['connect', 'verify', 'size', 'scope', 'watch'] as const

export const JOURNEY_STEP_COPY: Record<
  (typeof JOURNEY_STEP_ORDER)[number],
  { title: string; body: string }
> = {
  connect: {
    title: 'Connect',
    body: 'Your Microsoft 365 admin runs a short setup script from your Guardian team. It sets up the enterprise apps Guardian needs and grants them the roles and permissions in the permissions guide, most of them read-only. Apart from turning on Exchange organization customization if it’s off, and removing the Compliance Administrator role from Microsoft’s M365 Tenant Configuration Manager app unless your setup includes Purview compliance monitoring, it doesn’t change your policies or settings.',
  },
  verify: {
    title: 'Verify',
    body: 'Guardian checks the connection end to end and confirms every permission landed.',
  },
  size: {
    title: 'Size',
    body: 'We take a full capture of your configuration, so coverage is sized to your tenant.',
  },
  scope: {
    title: 'Choose scope',
    body: 'Together we choose the coverage that fits: identity and threat protection first, more if you want it.',
  },
  watch: {
    title: 'Watch',
    body: 'Continuous monitoring goes live. Changes show up here, ranked and explained.',
  },
}

/**
 * Which journey step is "current" for each server-computed onboarding state.
 * Finishing/Held sit on the Watch step (index 4): every earlier step is done,
 * and the watch step carries the non-interactive "Setting up"/held rung
 * (composeSettingUpRung). Active is past the end — every step renders done and
 * the journey itself is omitted.
 */
const JOURNEY_CURRENT_INDEX: Record<TcmOnboardingState, number> = {
  NotStarted: 0,
  ScriptReady: 1,
  Connected: 2,
  Sized: 3,
  Finishing: 4,
  Held: 4,
  Active: 5, // past the end — every step renders done; journey itself is omitted
}

const JOURNEY_HEADLINES: Record<Exclude<TcmOnboardingState, 'Active'>, { headline: string; sub: string }> = {
  NotStarted: {
    headline: 'Continuous monitoring is ready when you are.',
    sub: 'Five steps, most of them ours. Your part is one script run by your Microsoft 365 admin.',
  },
  ScriptReady: {
    headline: 'Your setup script is ready to run.',
    sub: 'Once your Microsoft 365 admin runs it, Guardian verifies the connection and keeps moving.',
  },
  Connected: {
    headline: 'Connected. Next we size your scope.',
    sub: 'Guardian takes a full capture of your configuration so coverage reflects what actually exists.',
  },
  Sized: {
    headline: 'Sized. Choose your coverage to go live.',
    sub: 'Your highest-impact controls are ready to watch. Continuous monitoring starts at the flip of a switch.',
  },
  // Self-serve auto-finish rungs (W5b D-1 / BR-3). The detailed, poll-driven
  // copy lives in composeSettingUpRung (rendered on the Watch step); these
  // headline/sub lines frame the whole journey section above the stepper.
  Finishing: {
    headline: 'Verified. We’re finishing your setup.',
    sub: 'No more steps for you. Guardian sizes your coverage and switches on the watch on its own.',
  },
  Held: {
    headline: 'Verified. Your team is finishing setup.',
    sub: 'Your connection is in. One part needs a person, so your Guardian team is finishing it for you.',
  },
}

/**
 * ExoProvisioning journey framing (Finding C). The Graph side is connected and
 * watched, but mail coverage is REQUIRED and still finishing — it needs one more
 * customer-driven run (the read-only service principal can't self-assign the
 * Exchange role). Verdict-first, honest about not-done, names the subject, and
 * never says "no more steps for you". Jargon-swept by the tests.
 */
const EXO_PROVISIONING_HEADLINE = {
  headline: 'Connected. Mail coverage needs one more run.',
  sub: 'Your identity, threat-protection, and device settings are watched now. One more run by your Microsoft 365 admin finishes mail coverage.',
} as const

/* -------------------------------------------------------------------------- */
/* Composer                                                                   */
/* -------------------------------------------------------------------------- */

const EMPTY_HERO: CoverageHero = {
  headline: 'We watch what ',
  headlineEm: 'matters.',
  deck: [],
  bylineMeta: 'Coverage',
}

export function composeMonitoringCoverage(input: ComposeCoverageInput): MonitoringCoverageView {
  const now = input.now ?? new Date()

  // Order matters: a missing tenant isn't an error, and loading wins over
  // both so the skeleton never flashes an empty state.
  if (!input.hasTenant) {
    return emptyView('no-tenant')
  }
  if (input.isLoading) {
    return emptyView('loading')
  }
  if (input.isError || !input.coverage) {
    return emptyView('error')
  }

  const c = input.coverage
  const scopeDate = formatLongDate(c.scopeAsOf)
  const captureDate = formatLongDate(c.lastFullCaptureAt)
  const lastCheck = formatRelativeAge(c.lastCheckAt, now)
  const watchedCount = formatSettingCount(c.watchedInstanceCount)
  const isHybridFamily = c.baselineSource === 'Hybrid' || c.baselineSource === 'TCM'

  return {
    state: 'ready',
    hero: composeHero(c, scopeDate),
    channels: composeChannels(c, { watchedCount, lastCheck, captureDate, isHybridFamily }),
    groups: c.monitoringActive ? composeGroups(c.groups) : null,
    groupsCaveat: c.monitoringActive ? composeGroupsCaveat(c.groups) : null,
    inventoryNote: INVENTORY_NOTE,
    lifecycle: [...LIFECYCLE_STEPS],
    journey: composeJourney(c),
  }
}

function emptyView(state: Exclude<CoverageViewState, 'ready'>): MonitoringCoverageView {
  return {
    state,
    hero: EMPTY_HERO,
    channels: [],
    groups: null,
    groupsCaveat: null,
    inventoryNote: INVENTORY_NOTE,
    lifecycle: [...LIFECYCLE_STEPS],
    journey: null,
  }
}

/* ---------------------------- Hero ---------------------------------------- */

function composeHero(c: TcmCoverageSummary, scopeDate: string | null): CoverageHero {
  const deck: string[] = []

  if (c.monitoringActive) {
    // Locked headline copy from the customer monitoring-scope draft —
    // approved language. The dash became a comma 2026-09-25 (em-dash tell);
    // the scope-copy doc carries the same edit.
    deck.push(
      'We watch the settings that decide your security posture, sized to what we found in your tenant.',
    )
    if (scopeDate) {
      deck.push(`Your monitored scope was sized from a full configuration capture taken on ${scopeDate}.`)
    } else {
      deck.push('We’re sizing your monitored scope.')
    }
    deck.push(
      'We focus continuous monitoring on your highest-impact identity, threat-protection, and device controls. That focus is deliberate, not a gap.',
    )
  } else {
    // Pre-activation (or scan-only tenant): the baseline-scan story leads,
    // and the journey below carries the path to continuous watch.
    deck.push(
      'Guardian compares your Microsoft 365 configuration against your approved baseline on every scan, and flags what moved.',
    )
    deck.push(
      'Continuous monitoring is ready to switch on for your tenant. The setup journey below shows the path.',
    )
  }

  return {
    headline: 'We watch what ',
    headlineEm: 'matters.',
    deck,
    bylineMeta: scopeDate ? `Scope sized ${scopeDate}` : 'Coverage',
  }
}

/* ---------------------------- Channels ------------------------------------ */

function composeChannels(
  c: TcmCoverageSummary,
  parts: {
    watchedCount: string | null
    lastCheck: string | null
    captureDate: string | null
    isHybridFamily: boolean
  },
): CoverageChannel[] {
  const channels: CoverageChannel[] = []

  // Channel 1 — continuous watch. Active only when the whole pipeline is
  // live; meta carries the real count + freshness, omitting whichever half
  // is unknown rather than inventing it. The meta is gated on liveness too:
  // a Sized-but-not-activated tenant has a real watch-list count, but
  // "under watch" on a Not-yet-live card would contradict itself (house
  // review 2026-06-11) — the journey's Scope/Watch steps carry that state.
  const continuousMeta = c.monitoringActive
    ? ([
        parts.watchedCount ? `${parts.watchedCount} under watch` : null,
        parts.lastCheck ? `last check ${parts.lastCheck}` : null,
      ].filter(Boolean) as string[])
    : []

  channels.push({
    id: 'continuous',
    title: 'Continuous watch',
    verdict: 'Checked several times a day.',
    // #18 S2 — the watch now catches brand-new policies between checks, not
    // only changes to ones it already knows. Said plainly so the coverage
    // promise matches what the queue actually surfaces (parent §4.2): a new
    // policy and a changed setting both land here.
    body:
      'Your highest-impact identity, threat-protection, and device settings, watched around the clock. ' +
      'When one changes, or a new policy appears, it lands in the queue with a severity and a recommendation.',
    meta: continuousMeta.length > 0 ? continuousMeta.join(' · ') : null,
    active: c.monitoringActive,
    inactiveNote: c.monitoringActive
      ? null
      : 'Not yet live for your tenant. The setup journey below shows the path.',
    caveat:
      c.monitoringActive && c.onboarding.exoProvisioning
        ? 'Mail coverage is still completing setup. Everything else is live.'
        : null,
  })

  // Channel 2 — baseline scans. On Hybrid-family tenants the scan lane owns
  // SharePoint + OneDrive; on scan-only tenants it owns the whole
  // configuration, so the copy widens honestly.
  channels.push(
    parts.isHybridFamily
      ? {
          id: 'scans',
          title: 'SharePoint & OneDrive scans',
          verdict: 'Reviewed on every scan.',
          body:
            'Site and sharing configuration compared against your approved baseline on every scheduled scan. ' +
            'Changes queue up alongside everything else.',
          meta: null,
          active: c.spoOdbCovered,
          inactiveNote: c.spoOdbCovered ? null : 'Scan coverage is not enabled for this tenant.',
          caveat: null,
        }
      : {
          id: 'scans',
          title: 'Baseline scans',
          verdict: 'Your whole configuration, every scan.',
          body:
            'Your Microsoft 365 configuration compared against your approved baseline on every scheduled scan. ' +
            'Changes queue up here with a severity and a recommendation.',
          meta: null,
          active: c.spoOdbCovered,
          inactiveNote: c.spoOdbCovered ? null : 'Scan coverage is not enabled for this tenant.',
          caveat: null,
        },
  )

  // Channel 3 — full-configuration capture. Three honest states, driven by real
  // data — never fabricate a rhythm (CLAUDE.md no-fabrication):
  //   • no capture yet (null date)        → "happens during setup", inactive.
  //   • a capture exists, no live rhythm   → static "complete record" + date
  //     (sweep dark / pre-pilot / a single historical sizing capture).
  //   • an established, fresh recurring sweep (`recurringSweepActive`)
  //     → "kept current" + the plain-English DYNAMIC copy: it recurs AND the
  //     rhythm is sized to the tenant. NO numbers, cadence, or quota — the
  //     boolean is the only signal; the cadence stays internal (PA-only meter).
  // `recurringSweepActive` is server-derived (≥2 recent archived sweeps), so the
  // recurring-rhythm promise renders ONLY when it is genuinely true; we also
  // require a real capture date so the meta and the promise can't disagree.
  // The copy names NO frequency ("every few days" etc.) on purpose: the sweep
  // cadence is sized per-tenant (TcmBudget.ComputeSnapshotSweepCadenceDays —
  // ~2 days for small tenants, but legitimately weekly/monthly for very large
  // config scopes), so a fixed-frequency claim would be false for big tenants
  // (codex P2, 2026-06-16). "Recurring rhythm that adapts to your size" is true
  // for every actively-swept tenant regardless of the real cadence.
  const sweepActive = c.recurringSweepActive && !!parts.captureDate
  channels.push({
    id: 'capture',
    title: 'Full-configuration capture',
    verdict: sweepActive ? 'Your configuration, kept current.' : 'The complete record.',
    body: sweepActive
      ? 'Guardian re-captures your full security configuration on a recurring rhythm that adapts ' +
        'to the size of your environment, so your record stays current as you grow.'
      : 'A complete record of your tenant’s configuration, kept by Guardian. ' +
        'It sizes your monitored scope, so coverage reflects what actually exists in your tenant.',
    meta: parts.captureDate ? `Last full capture ${parts.captureDate}` : null,
    active: !!parts.captureDate,
    inactiveNote: parts.captureDate ? null : 'Your first full capture happens during setup.',
    caveat: null,
  })

  return channels
}

/* ---------------------------- Groups -------------------------------------- */

function composeGroups(groups: TcmCoverageGroup[]): CoverageGroupRow[] {
  return groups
    .filter(
      (g): g is TcmCoverageGroup & { group: Exclude<TcmMonitoringGroup, 'inventory'> } =>
        g.group !== 'inventory' && g.group in GROUP_COPY,
    )
    .map(g => ({
      group: g.group,
      title: GROUP_COPY[g.group].title,
      description: GROUP_COPY[g.group].description,
      watched: g.watched,
      countLabel: g.watched ? formatSettingCount(g.watchedInstanceCount) : null,
      settings: g.watched ? composeGroupSettings(g.types) : null,
    }))
}

function composeGroupSettings(
  types: TcmCoverageGroup['types'],
): CoverageGroupSettings[] | null {
  if (!types || types.length === 0) return null
  return types.map(t => ({
    id: t.resourceType,
    typeLabel: formatTcmResourceType(t.resourceType),
    count: t.count,
    names: t.names,
  }))
}

/**
 * One caveat for the whole group section. `watched: null` means the watch
 * list could not be read this request — say so rather than render confident
 * "not in scope" rows (the unknown state is the honest one).
 */
function composeGroupsCaveat(groups: TcmCoverageGroup[]): string | null {
  const anyUnknown = groups.some(g => g.group !== 'inventory' && g.watched === null)
  return anyUnknown
    ? 'We could not read your watch list just now, so group detail is incomplete. Refresh in a moment.'
    : null
}

/* ---------------------------- Journey -------------------------------------- */

function composeJourney(c: TcmCoverageSummary): CoverageJourney | null {
  const state = c.onboarding.state
  // Defensive: an Active tenant normally has no journey to render — EXCEPT one
  // still in ExoProvisioning (mail coverage not finished). Under Option A a NEW
  // tenant can't reach Hybrid/Active while ExoProvisioning, but an existing or
  // migrated row (the Phase A rename ExoIncomplete→ExoProvisioning could leave a
  // tenant already Active) must still get the run-2 finish journey rather than
  // fall into the "done" early-return that would hide the required step.
  if (state === 'Active' && !c.onboarding.exoProvisioning) return null

  // ExoProvisioning: the Graph side is connected and watched, but mail coverage
  // needs a customer-driven run 2 to finish (the read-only service principal can't
  // self-assign the Exchange role). The server may report this tenant as
  // Connected/Sized/Finishing under the hood — but that would pin the stepper to
  // Size/Scope/Watch and headline it "no more steps for you", hiding a REQUIRED
  // step. Override: pin the current step to Verify so the interactive run-2
  // affordance (composeJourneyActions returns the 'verify' action) has a coherent
  // home, and headline it honestly as "not done — mail needs one more run".
  const exoProvisioning = c.onboarding.exoProvisioning
  const currentIndex = exoProvisioning
    ? JOURNEY_STEP_ORDER.indexOf('verify')
    : JOURNEY_CURRENT_INDEX[state] ?? 0
  const steps: JourneyStep[] = JOURNEY_STEP_ORDER.map((id, i) => ({
    id,
    title: JOURNEY_STEP_COPY[id].title,
    body: JOURNEY_STEP_COPY[id].body,
    state: i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'todo',
  }))

  const { headline, sub } = exoProvisioning ? EXO_PROVISIONING_HEADLINE : JOURNEY_HEADLINES[state]

  // A stalled verification surfaces as a plain-language caveat. The raw
  // server error is operator material — it stays out of the customer surface.
  // Suppressed on the self-serve finish phases (Finishing/Held): the
  // non-interactive rung carries the honest state there, and a "connection
  // check hit a snag" line would read as a re-verify prompt the customer can't
  // satisfy (the verify seed is gone) — exactly the GAP-6 trap. Also suppressed
  // on ExoProvisioning: the run-2 verify affordance IS the customer's next
  // action, so a "hit a snag" line would just muddy it.
  const isFinishPhase = state === 'Finishing' || state === 'Held'
  const caveat =
    !exoProvisioning && !isFinishPhase && c.onboarding.lastError
      ? 'The last connection check hit a snag. Your Guardian team has the detail and is on it.'
      : null

  return { headline, sub, steps, caveat }
}
